import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog as _Dialog2, // eslint-disable-line no-unused-vars
} from "@/components/ui/dialog";

/**
 * DriverFormDialog — one dialog reused for both CREATE (mode='create') and
 * EDIT (mode='edit'). Emits onSaved(driver) after success so the caller can
 * mutate its SWR cache.
 */

// Module-level: defining InputField outside the component prevents React
// from creating a NEW component type on every keystroke (which was
// unmounting inputs after each character and dropping focus).
const InputField = ({ k, label, opts = {}, value, onChange }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-brand-text-muted">
      {label}{opts.required && " *"}
    </Label>
    <Input
      required={opts.required}
      type={opts.type || "text"}
      value={value ?? ""}
      onChange={(e) => onChange(k, e.target.value)}
      placeholder={opts.placeholder || ""}
      data-testid={`driver-form-${k}`}
    />
  </div>
);
const FIELDS = {
  identity: [
    ["driver_code", "Driver ID", { required: true, placeholder: "BAY001" }],
    ["full_name", "Full name", { required: true }],
    ["email", "Email", { type: "email" }],
    ["phone", "Phone"],
    ["ni_number", "NI number"],
    ["utr_number", "UTR number"],
    ["depot", "Depot"],
    ["assigned_vehicle", "Assigned vehicle"],
    ["start_date", "Start date", { type: "date" }],
  ],
  banking: [
    ["bank_name", "Bank name"],
    ["account_name", "Account name"],
    ["sort_code", "Sort code", { placeholder: "20-00-00" }],
    ["account_number", "Account number"],
  ],
};

const STATUSES = ["active", "inactive", "suspended", "on_holiday", "off", "pending_documents"];

const emptyDriver = () => ({
  driver_code: "", full_name: "", email: "", phone: "", ni_number: "", utr_number: "",
  start_date: "", status: "active", depot: "", assigned_vehicle: "",
  pay_type: "fixed", daily_rate: "", vehicle_fee: "", fuel_fee: "",
  management_fee: "", insurance_fee: "",
  bank_name: "", account_name: "", sort_code: "", account_number: "",
});

export const DriverFormDialog = ({ open, onOpenChange, mode = "create", driver = null, onSaved }) => {
  const [form, setForm] = useState(emptyDriver());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode === "edit" && driver) {
      const populated = { ...emptyDriver() };
      Object.keys(populated).forEach((k) => {
        if (driver[k] !== undefined && driver[k] !== null) populated[k] = driver[k];
      });
      setForm(populated);
    } else if (mode === "create") {
      const populated = { ...emptyDriver() };
      if (driver) {
        // Support DPD import's "quick-create" flow — pre-fill driver_code
        // (or any other supplied fields) so the operator doesn't retype them.
        Object.keys(populated).forEach((k) => {
          if (driver[k] !== undefined && driver[k] !== null) populated[k] = driver[k];
        });
      }
      setForm(populated);
    }
  }, [mode, driver, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.driver_code || !form.full_name) {
      toast.error("Driver ID and full name are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, driver_code: form.driver_code.trim().toUpperCase() };
      ["daily_rate", "vehicle_fee", "fuel_fee", "management_fee", "insurance_fee"].forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] === null ? 0 : parseFloat(payload[k]);
      });
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      let result;
      if (mode === "edit") {
        const { data } = await api.patch(`/drivers/${driver.id}`, payload);
        result = data;
        toast.success("Driver updated");
      } else {
        const { data } = await api.post("/drivers", payload);
        result = data;
        toast.success(`Driver ${payload.driver_code} added`);
      }
      onOpenChange(false);
      onSaved?.(result);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? `Edit ${driver?.full_name || "driver"}` : "New driver"}</DialogTitle>
          <DialogDescription>
            {mode === "edit"
              ? "Update subcontractor details. Pay rule changes take effect from the next work row imported."
              : "Create a subcontractor profile. Pay rules can be edited later."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-text-muted">Identity</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.identity.map(([k, label, opts]) => (
              <InputField key={k} k={k} label={label} opts={opts} value={form[k]} onChange={set} />
            ))}
            <div>
              <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="driver-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-text-muted pt-3">Pay rules</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Pay type</Label>
              <Select value={form.pay_type} onValueChange={(v) => set("pay_type", v)}>
                <SelectTrigger data-testid="driver-form-paytype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed daily</SelectItem>
                  <SelectItem value="dpd_based">DPD based (route)</SelectItem>
                  <SelectItem value="fixed_plus_fees">Fixed + vehicle/fuel/mgmt fees</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.pay_type !== "dpd_based" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Daily rate (£)</Label>
                <Input type="number" step="0.01" value={form.daily_rate ?? ""}
                  onChange={(e) => set("daily_rate", e.target.value)} data-testid="driver-form-daily_rate" />
              </div>
            )}
            {form.pay_type === "fixed_plus_fees" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Vehicle fee /day</Label>
                  <Input type="number" step="0.01" value={form.vehicle_fee ?? ""}
                    onChange={(e) => set("vehicle_fee", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Fuel fee /day</Label>
                  <Input type="number" step="0.01" value={form.fuel_fee ?? ""}
                    onChange={(e) => set("fuel_fee", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Management /day</Label>
                  <Input type="number" step="0.01" value={form.management_fee ?? ""}
                    onChange={(e) => set("management_fee", e.target.value)} />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Insurance /day</Label>
              <Input type="number" step="0.01" value={form.insurance_fee ?? ""}
                onChange={(e) => set("insurance_fee", e.target.value)} />
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-text-muted pt-3">Banking (for invoice payments)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.banking.map(([k, label, opts]) => (
              <InputField key={k} k={k} label={label} opts={opts || {}} value={form[k]} onChange={set} />
            ))}
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-brand-red hover:bg-brand-red-hover" data-testid="driver-form-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {mode === "edit" ? "Save changes" : "Create driver"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DriverFormDialog;
